import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const RateCalculatorModal = ({ isOpen, onClose }) => {
  const [sendAmount, setSendAmount] = useState('1000');
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('EUR');
  const [calculationResult, setCalculationResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // Mock exchange rate data with real-time simulation
  const exchangeRates = {
    USD: { EUR: 0.92, GBP: 0.79, CAD: 1.35, MXN: 17.2, PHP: 56.5, INR: 83.2, AED: 3.67, EGP: 30.75, KRW: 1320, JPY: 149.5, AUD: 1.52, SAR: 3.75 },
    EUR: { USD: 1.09, GBP: 0.86, CAD: 1.47, MXN: 18.7, PHP: 61.5, INR: 90.6, AED: 4.0, EGP: 33.5, KRW: 1437, JPY: 162.8, AUD: 1.66, SAR: 4.08 },
    GBP: { USD: 1.27, EUR: 1.16, CAD: 1.71, MXN: 21.8, PHP: 71.7, INR: 105.6, AED: 4.66, EGP: 39.0, KRW: 1675, JPY: 189.6, AUD: 1.93, SAR: 4.76 },
    CAD: { USD: 0.74, EUR: 0.68, GBP: 0.58, MXN: 12.7, PHP: 41.9, INR: 61.6, AED: 2.72, EGP: 22.8, KRW: 977, JPY: 110.7, AUD: 1.13, SAR: 2.78 },
    MXN: { USD: 0.058, EUR: 0.053, GBP: 0.046, CAD: 0.079, PHP: 3.29, INR: 4.84, AED: 0.21, EGP: 1.79, KRW: 76.7, JPY: 8.69, AUD: 0.088, SAR: 0.22 },
    PHP: { USD: 0.0177, EUR: 0.0163, GBP: 0.0139, CAD: 0.024, MXN: 0.30, INR: 1.47, AED: 0.065, EGP: 0.54, KRW: 23.3, JPY: 2.64, AUD: 0.027, SAR: 0.066 },
    INR: { USD: 0.012, EUR: 0.011, GBP: 0.0095, CAD: 0.016, MXN: 0.21, PHP: 0.68, AED: 0.044, EGP: 0.37, KRW: 15.9, JPY: 1.80, AUD: 0.018, SAR: 0.045 },
    AED: { USD: 0.27, EUR: 0.25, GBP: 0.21, CAD: 0.37, MXN: 4.69, PHP: 15.4, INR: 22.7, EGP: 8.38, KRW: 359, JPY: 40.7, AUD: 0.41, SAR: 1.02 },
    EGP: { USD: 0.0325, EUR: 0.030, GBP: 0.026, CAD: 0.044, MXN: 0.56, PHP: 1.84, INR: 2.71, AED: 0.119, KRW: 42.9, JPY: 4.86, AUD: 0.049, SAR: 0.122 },
    KRW: { USD: 0.000758, EUR: 0.000696, GBP: 0.000597, CAD: 0.00102, MXN: 0.013, PHP: 0.043, INR: 0.063, AED: 0.0028, EGP: 0.023, JPY: 0.113, AUD: 0.00115, SAR: 0.00284 },
    JPY: { USD: 0.0067, EUR: 0.0061, GBP: 0.0053, CAD: 0.0090, MXN: 0.115, PHP: 0.379, INR: 0.556, AED: 0.025, EGP: 0.206, KRW: 8.83, AUD: 0.010, SAR: 0.025 },
    AUD: { USD: 0.66, EUR: 0.60, GBP: 0.52, CAD: 0.88, MXN: 11.3, PHP: 37.2, INR: 54.7, AED: 2.42, EGP: 20.2, KRW: 868, JPY: 98.3, SAR: 2.47 },
    SAR: { USD: 0.27, EUR: 0.25, GBP: 0.21, CAD: 0.36, MXN: 4.59, PHP: 15.1, INR: 22.2, AED: 0.98, EGP: 8.20, KRW: 352, JPY: 39.9, AUD: 0.40 }
  };

  const currencies = [
    { value: 'USD', label: 'USD - US Dollar', flag: '🇺🇸' },
    { value: 'EUR', label: 'EUR - Euro', flag: '🇪🇺' },
    { value: 'GBP', label: 'GBP - British Pound', flag: '🇬🇧' },
    { value: 'CAD', label: 'CAD - Canadian Dollar', flag: '🇨🇦' },
    { value: 'MXN', label: 'MXN - Mexican Peso', flag: '🇲🇽' },
    { value: 'PHP', label: 'PHP - Philippine Peso', flag: '🇵🇭' },
    { value: 'INR', label: 'INR - Indian Rupee', flag: '🇮🇳' },
    { value: 'AED', label: 'AED - UAE Dirham', flag: '🇦🇪' },
    { value: 'EGP', label: 'EGP - Egyptian Pound', flag: '🇪🇬' },
    { value: 'KRW', label: 'KRW - South Korean Won', flag: '🇰🇷' },
    { value: 'JPY', label: 'JPY - Japanese Yen', flag: '🇯🇵' },
    { value: 'AUD', label: 'AUD - Australian Dollar', flag: '🇦🇺' },
    { value: 'SAR', label: 'SAR - Saudi Riyal', flag: '🇸🇦' }
  ];

  const calculateTransfer = () => {
    if (!sendAmount || parseFloat(sendAmount) <= 0) {
      return;
    }

    setIsCalculating(true);

    // Simulate API call delay
    setTimeout(() => {
      const amount = parseFloat(sendAmount);
      const rate = exchangeRates?.[fromCurrency]?.[toCurrency] || 1;
      
      // Calculate fees based on amount and currencies
      const baseFee = amount < 500 ? 5 : amount < 1000 ? 10 : amount < 5000 ? 15 : 25;
      const percentageFee = amount * 0.015; // 1.5% of amount
      const totalFee = baseFee + percentageFee;
      
      // Calculate amounts
      const amountAfterFee = amount - totalFee;
      const receiveAmount = amountAfterFee * rate;
      
      // Agent commission (35% of fees)
      const agentCommission = totalFee * 0.35;
      
      setCalculationResult({
        sendAmount: amount,
        fromCurrency,
        toCurrency,
        exchangeRate: rate,
        baseFee,
        percentageFee,
        totalFee,
        receiveAmount,
        agentCommission,
        effectiveRate: receiveAmount / amount,
        estimatedTime: fromCurrency === toCurrency ? 'Instant' : 
                       ['USD', 'EUR', 'GBP']?.includes(fromCurrency) && ['USD', 'EUR', 'GBP']?.includes(toCurrency) ? '1-2 hours' :
                       '2-4 business days'
      });
      
      setIsCalculating(false);
    }, 500);
  };

  useEffect(() => {
    if (sendAmount && parseFloat(sendAmount) > 0) {
      calculateTransfer();
    }
  }, [sendAmount, fromCurrency, toCurrency]);

  const handleSwapCurrencies = () => {
    const temp = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(temp);
  };

  const handleQuickAmount = (amount) => {
    setSendAmount(amount?.toString());
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Exchange Rate Calculator</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Calculate transfer amounts and commission in real-time
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Calculator Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Input */}
            <div className="space-y-6">
              <div className="bg-muted/50 rounded-lg p-6 space-y-4">
                <h3 className="font-semibold text-foreground">Transfer Details</h3>
                
                {/* Send Amount */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Amount to Send
                  </label>
                  <Input
                    type="number"
                    value={sendAmount}
                    onChange={(e) => setSendAmount(e?.target?.value)}
                    placeholder="Enter amount"
                    min="0"
                    step="0.01"
                  />
                  <div className="flex items-center space-x-2 mt-2">
                    <span className="text-xs text-muted-foreground">Quick amounts:</span>
                    {[100, 500, 1000, 5000]?.map(amount => (
                      <button
                        key={amount}
                        onClick={() => handleQuickAmount(amount)}
                        className="px-2 py-1 text-xs bg-background border border-border rounded hover:bg-muted transition-colors"
                      >
                        {amount}
                      </button>
                    ))}
                  </div>
                </div>

                {/* From Currency */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    From Currency
                  </label>
                  <Select
                    value={fromCurrency}
                    onChange={(e) => setFromCurrency(e?.target?.value)}
                    options={currencies?.map(c => ({
                      value: c?.value,
                      label: `${c?.flag} ${c?.label}`
                    }))}
                  />
                </div>

                {/* Swap Button */}
                <div className="flex justify-center">
                  <button
                    onClick={handleSwapCurrencies}
                    className="w-10 h-10 bg-primary/10 hover:bg-primary/20 rounded-full flex items-center justify-center transition-colors"
                    title="Swap currencies"
                  >
                    <Icon name="ArrowDownUp" size={20} className="text-primary" />
                  </button>
                </div>

                {/* To Currency */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    To Currency
                  </label>
                  <Select
                    value={toCurrency}
                    onChange={(e) => setToCurrency(e?.target?.value)}
                    options={currencies?.map(c => ({
                      value: c?.value,
                      label: `${c?.flag} ${c?.label}`
                    }))}
                  />
                </div>
              </div>

              {/* Exchange Rate Info */}
              {calculationResult && (
                <div className="bg-info/10 border border-info/20 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="TrendingUp" size={16} className="text-info" />
                    <span className="text-sm font-medium text-info">Live Exchange Rate</span>
                  </div>
                  <div className="text-lg font-semibold text-foreground">
                    1 {fromCurrency} = {calculationResult?.exchangeRate?.toFixed(4)} {toCurrency}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Rates updated in real-time • Estimated delivery: {calculationResult?.estimatedTime}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Results */}
            <div className="space-y-6">
              {isCalculating ? (
                <div className="bg-muted/50 rounded-lg p-6 flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Calculating...</p>
                  </div>
                </div>
              ) : calculationResult ? (
                <>
                  {/* Calculation Breakdown */}
                  <div className="bg-muted/50 rounded-lg p-6 space-y-4">
                    <h3 className="font-semibold text-foreground">Calculation Breakdown</h3>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between items-center pb-2 border-b border-border">
                        <span className="text-sm text-muted-foreground">Send Amount</span>
                        <span className="text-sm font-medium text-foreground">
                          {calculationResult?.sendAmount?.toFixed(2)} {fromCurrency}
                        </span>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground pl-4">Base Fee</span>
                          <span className="text-sm text-foreground">
                            -{calculationResult?.baseFee?.toFixed(2)} {fromCurrency}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground pl-4">Service Fee (1.5%)</span>
                          <span className="text-sm text-foreground">
                            -{calculationResult?.percentageFee?.toFixed(2)} {fromCurrency}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pb-2 border-b border-border font-medium">
                          <span className="text-sm text-foreground">Total Fees</span>
                          <span className="text-sm text-error">
                            -{calculationResult?.totalFee?.toFixed(2)} {fromCurrency}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Amount to Convert</span>
                        <span className="text-sm font-medium text-foreground">
                          {(calculationResult?.sendAmount - calculationResult?.totalFee)?.toFixed(2)} {fromCurrency}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center pt-2 border-t border-border">
                        <span className="text-base font-semibold text-foreground">Recipient Gets</span>
                        <span className="text-lg font-bold text-success">
                          {calculationResult?.receiveAmount?.toFixed(2)} {toCurrency}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Agent Commission */}
                  <div className="bg-success/10 border border-success/20 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Icon name="DollarSign" size={20} className="text-success" />
                        <span className="font-semibold text-foreground">Your Commission</span>
                      </div>
                      <span className="text-2xl font-bold text-success">
                        ${calculationResult?.agentCommission?.toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      35% of total fees • Paid on transaction completion
                    </p>
                  </div>

                  {/* Effective Rate */}
                  <div className="bg-background rounded-lg p-4 border border-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-muted-foreground">Effective Rate</div>
                        <div className="text-xs text-muted-foreground">Including all fees</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-foreground">
                          1 {fromCurrency} = {calculationResult?.effectiveRate?.toFixed(4)} {toCurrency}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          ({((calculationResult?.effectiveRate / calculationResult?.exchangeRate - 1) * 100)?.toFixed(2)}% from market rate)
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Info Banner */}
                  <div className="bg-info/10 border border-info/20 rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <Icon name="Info" size={16} className="text-info mt-0.5" />
                      <div className="text-xs text-info/80">
                        <p className="mb-1">
                          <strong>Estimated delivery time:</strong> {calculationResult?.estimatedTime}
                        </p>
                        <p>
                          Exchange rates may fluctuate. Final rate locked at transfer initiation.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-muted/50 rounded-lg p-6 flex items-center justify-center h-full">
                  <div className="text-center">
                    <Icon name="Calculator" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Enter amount to see calculation</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex justify-between">
          <div className="text-xs text-muted-foreground flex items-center space-x-1">
            <Icon name="Shield" size={12} />
            <span>Rates provided for reference only</span>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={onClose}>Close</Button>
            {calculationResult && (
              <Button variant="primary" onClick={() => {}}>
                Proceed with Transfer
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RateCalculatorModal;